import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";

const quickRules = [
	{
		title: "Estados del curso",
		icon: "ri-git-branch-fill",
		items: [
			"Abierto: permite matricular, editar contenido, agregar grabaciones, avanzar y rendir examen.",
			"Finalizado: permite consulta y revisión; no permite cambios docentes ni nuevas matrículas.",
			"Cerrado: bloquea acceso de estudiantes y docentes; queda disponible como historial administrativo.",
		],
	},
	{
		title: "Notas y aprobación",
		icon: "ri-bar-chart-box-fill",
		items: [
			"La nota final usa escala chilena de 1.0 a 7.0.",
			"Desde 4.0 el estudiante queda aprobado; bajo 4.0 queda reprobado.",
			"El puntaje del examen se calcula según preguntas correctas y luego se transforma a nota.",
		],
	},
	{
		title: "Certificados",
		icon: "ri-award-fill",
		items: [
			"Solo se descargan si el curso está Finalizado o Cerrado y la emisión está habilitada.",
			"El estudiante debe estar Completado y tener nota igual o superior a 4.0.",
			"El envío masivo solo se permite antes de cerrar el curso.",
		],
	},
	{
		title: "Organizaciones",
		icon: "ri-building-4-fill",
		items: [
			"Una organización desactivada conserva historial, pero no permite acciones operativas.",
			"Al desactivar una organización se desactivan sus usuarios, representantes, contratos y asignaciones de curso activas; no se desactivan las plantillas de curso del catálogo.",
			"Reactivar una organización no reactiva automáticamente sus dependientes; deben revisarse uno a uno.",
		],
	},
	{
		title: "Registros inactivos",
		icon: "ri-lock-2-fill",
		items: [
			"Un registro inactivo queda en modo histórico/reactivable.",
			"La única acción permitida sobre una organización o usuario inactivo es activar/reactivar.",
			"Las acciones no permitidas deben verse deshabilitadas y explicar el motivo dentro del flujo correspondiente.",
		],
	},
];

const courseStateMatrix = [
	{
		state: "Abierto",
		admin: "Gestiona curso, contenido, matrícula, grabaciones y certificados no emitibles.",
		teacher: "Accede, edita contenido, habilita examen, revisa notas y mensajes.",
		student: "Accede al aula, marca avance, consulta material y rinde examen si cumple requisitos.",
		company: "Puede matricular si contrato/cupo están vigentes.",
	},
	{
		state: "Finalizado",
		admin: "Revisa resultados, habilita emisión y descarga certificados individuales.",
		teacher: "Accede en modo lectura; no edita ni resetea exámenes.",
		student: "Accede en modo consulta dentro del periodo permitido; no genera nuevo avance.",
		company: "Lo ve como historial/no disponible; no matrícula.",
	},
	{
		state: "Cerrado",
		admin: "Consulta historial y puede descargar certificados individuales si emisión está habilitada.",
		teacher: "No accede al curso.",
		student: "No accede al aula del curso.",
		company: "Lo ve como historial/no disponible; no matrícula.",
	},
];

const tableProcedures = [
	{
		title: "Buscar registros",
		icon: "ri-search-line",
		steps: [
			"Entrar al módulo correspondiente desde el menú lateral.",
			"Ubicar el campo de búsqueda sobre la tabla.",
			"Escribir código, nombre, RUT, estado u otro dato disponible en la pantalla.",
			"Revisar que la tabla se actualice con los resultados encontrados.",
			"Usar el botón de limpiar búsqueda cuando se necesite volver al listado completo.",
		],
		notes: [
			"Si no aparece un registro, confirmar que pertenece al módulo correcto y que no existe un filtro activo.",
		],
	},
	{
		title: "Ordenar, filtrar y paginar",
		icon: "ri-sort-desc",
		steps: [
			"Seleccionar la cantidad de filas que se quieren ver por página.",
			"Elegir la columna de ordenamiento cuando la pantalla lo permita.",
			"Cambiar entre Asc o Desc según el orden requerido.",
			"Usar los controles de página para avanzar, retroceder o volver al inicio.",
			"Presionar Reiniciar para volver a la configuracion inicial del listado.",
		],
		notes: [
			"Los filtros y ordenadores solo cambian la visualización; no modifican los datos guardados.",
		],
	},
];

const modules = [
	{
		id: "panel",
		title: "Panel",
		icon: "ri-dashboard-fill",
		path: "/backoffice",
		purpose: "Entrega una vista general para iniciar la administración del sistema.",
		actions: [
			"Revisar indicadores generales del entorno administrativo.",
			"Acceder rápidamente a los módulos principales desde el menú lateral.",
			"Validar que el usuario conectado corresponda al administrador correcto.",
		],
		rules: [
			"Solo usuarios con rol Admin pueden acceder al backoffice.",
			"Los contadores principales muestran entidades operativas: no consideran organizaciones desactivadas ni el superusuario global.",
			"El contador de usuarios excluye superusuarios y usuarios asociados a organizaciones desactivadas.",
		],
		support: [
			"Si un usuario no entra al panel, validar rol activo, credenciales y estado del usuario.",
			"Si el menú no muestra una sección, confirmar que la ruta esté protegida para Admin.",
		],
	},
	{
		id: "organizaciones",
		title: "Organizaciones",
		icon: "ri-building-2-fill",
		path: "/backoffice/companies",
		purpose: "Administra empresas, representantes, contratos, cursos asociados y estudiantes vinculados.",
		actions: [
			"Crear organizaciones y revisar su ficha con RUT, nombre, actividad y cursos asociados.",
			"Crear o administrar representantes de empresa.",
			"Asignar cursos/contratos con cupos y fechas de vigencia.",
			"Revisar lotes de carga y estudiantes vinculados a la organización.",
		],
		rules: [
			"El RUT de la organización es obligatorio, debe ser chileno válido y único.",
			"Una organización activa puede operar mientras tenga condiciones válidas de acceso.",
			"Una organización desactivada conserva historial, pero bloquea acciones operativas; solo puede activarse.",
			"Al desactivar una organización se desactivan sus usuarios asociados, representantes, contratos marco y asignaciones de curso activas; no se desactivan las plantillas de curso del catálogo.",
			"Reactivar la organización no reactiva automáticamente usuarios, representantes, contratos ni cursos asignados.",
			"El representante solo matrícula en cursos disponibles para su empresa.",
			"Un curso disponible requiere contrato activo, cupo disponible y estado Abierto.",
			"Los cursos vencidos, finalizados o cerrados pueden mostrarse como historial, pero no aceptan matrícula.",
		],
		support: [
			"Si un representante ve un curso como No disponible, revisar cupo, vigencia, estado del run y si el contrato está activo.",
			"Si dos empresas ven cursos iguales, revisar si se les asignó el mismo contrato/curso y fechas equivalentes.",
			"Si un estudiante no aparece en una empresa, validar que su usuario tenga asociada la organización correcta.",
		],
	},
	{
		id: "usuarios",
		title: "Usuarios",
		icon: "ri-user-3-fill",
		path: "/backoffice/users",
		purpose: "Centraliza la administración de administradores, docentes, representantes y estudiantes.",
		actions: [
			"Crear usuarios administrativos y docentes.",
			"Revisar fichas de usuario, rol, RUT, correo, estado y organización asociada.",
			"Editar datos básicos, activar/desactivar accesos y resetear contraseñas cuando corresponda.",
			"Consultar historial académico de estudiantes y relación con cursos, certificados y lotes.",
		],
		rules: [
			"Desde este módulo solo se crean administradores y docentes; representantes se crean desde la ficha de organización y estudiantes desde flujos de carga/matrícula.",
			"Cada usuario debe operar con el rol que corresponde a su portal.",
			"Los estudiantes se gestionan principalmente desde el flujo de organizaciones/cargas.",
			"El estado del usuario controla acceso al sistema; el estado de matrícula controla acceso académico.",
			"Un usuario desactivado conserva historial, pero solo puede reactivarse antes de editar o ejecutar acciones como reiniciar contraseña.",
			"No se puede reactivar un usuario asociado a una organización desactivada; primero debe activarse la organización.",
			"Administradores y docentes pertenecen operativamente a Somnus y no deben quedar asociados a organizaciones cliente.",
		],
		support: [
			"Si un usuario no inicia sesión, revisar credenciales, estado activo, rol y portal correcto.",
			"Si falta información en una ficha, validar que el usuario tenga RUT, rol y organización cuando aplique.",
			"En entorno local puede usarse una contraseña simple definida por administración.",
		],
	},
	{
		id: "cursos",
		title: "Cursos",
		icon: "ri-book-2-fill",
		path: "/backoffice/courses",
		purpose: "Define la oferta académica, sus ediciones, contenido base, docente, fechas y reglas de certificación.",
		actions: [
			"Crear cursos con código, nombre, imagen, modalidad, duración, precio y descripción.",
			"Asignar docente, fechas, cupos, horario y requisitos de aprobación.",
			"Consultar información de catálogo, edición base, contenido y reglas de certificación.",
			"Editar información académica mientras el curso lo permita.",
		],
		rules: [
			"Para publicar un curso se exige código único válido, nombre, descripción, objetivos, duración, imagen, modalidad, docente activo, fechas, horario, URL de postulación y cupos.",
			"El código del curso debe tener 2 a 10 caracteres y usar solo letras, números o guion.",
			"El código del curso pertenece al catálogo Somnus; no debe incluir prefijos ni referencias a una organización cliente.",
			"Una misma plantilla de curso puede asignarse a varias organizaciones dentro de su periodo de postulación y vigencia contractual.",
			"Una organización puede cargar y matricular estudiantes después de tener el curso asignado, incluso si el curso aún no inicia.",
			"El estudiante matriculado solo puede acceder al aula desde la fecha de inicio del curso.",
			"El detalle de catálogo no debe consolidar estudiantes inscritos de varias organizaciones.",
			"Los estudiantes, notas y certificados deben revisarse desde el contexto de organización/asignación o run operativo correspondiente.",
			"La tabla de cursos muestra cuántas organizaciones activas tienen postulaciones/asignaciones y cuántas están En curso, Finalizadas o Cerradas.",
			"Postulaciones cuenta organizaciones con el curso asignado; En curso solo aumenta cuando la fecha actual está dentro del periodo de inicio y término del curso.",
			"Cuando la fecha de término del curso ya pasó, los estudiantes conservan 30 días de gracia; la edición se cierra automáticamente después de terminar ese periodo.",
			"El periodo de vigencia y el curso asignado se consideran vencidos solo después de terminar los 30 días de gracia.",
			"Si se deshabilita el último curso activo de un periodo, el periodo también debe quedar deshabilitado.",
			"No se puede reactivar un curso asignado si su periodo de vigencia está inactivo; primero debe habilitarse el periodo.",
			"No se puede desactivar un curso de catálogo si tiene organizaciones con el curso En curso o Finalizado.",
			"Solo se puede desactivar un curso de catálogo cuando todas sus organizaciones asociadas están Cerradas, o cuando no tiene organizaciones asociadas.",
			"Los cursos asincrónicos no requieren ni permiten horario semanal.",
			"Las modalidades presencial, sincrónica y mixta deben tener horario semanal definido.",
			"La fecha de término no puede ser anterior al inicio del curso.",
			"El orden de fechas debe ser: inicio de postulación, cierre de postulación, inicio del curso y término del curso.",
			"La fecha de inicio del curso debe ser posterior al cierre de postulación.",
			"Los cupos y la duración deben ser mayores a 0.",
			"Abierto permite operación académica completa.",
			"Finalizado permite revisión y consulta, pero no edición docente.",
			"Cerrado bloquea acceso docente y estudiante; se mantiene para historial y elimina archivos/contenidos subidos para recuperar espacio.",
			"El examen final respeta el límite de intentos configurado antes de iniciar un nuevo intento.",
			"El docente puede cargar una evaluación formativa opcional por unidad; funciona con preguntas Aiken igual que el examen final.",
			"Las evaluaciones formativas tienen intentos ilimitados por defecto.",
			"Al revisar una evaluación formativa, el estudiante debe ver puntaje, nota, respuestas correctas e identificación de respuestas incorrectas o no respondidas.",
			"El estado del catálogo activo/inactivo no es lo mismo que el estado del run Abierto/Finalizado/Cerrado.",
			"Desactivar un curso de catálogo no elimina ediciones, matrículas, certificados ni historial.",
			"Al desactivar un curso de catálogo se desactivan sus asignaciones activas a organizaciones.",
			"Un curso de catálogo inactivo solo puede activarse; las demás acciones operativas deben permanecer deshabilitadas.",
		],
		support: [
			"Si un docente no puede editar, revisar si el curso está Finalizado o Cerrado.",
			"Si un estudiante no accede, revisar matrícula, estado del curso, periodo de acceso y estado de usuario.",
			"Si las notas no cuadran, revisar intentos de examen y nota final almacenada en matrícula.",
		],
	},
	{
		id: "clases",
		title: "Clases grabadas",
		icon: "ri-video-on-fill",
		path: "/backoffice/recorded-classes",
		purpose: "Gestiona enlaces de grabaciones asociados a cursos activos.",
		actions: [
			"Listar cursos activos que pueden tener historial de grabaciones.",
			"Agregar, editar, publicar o eliminar enlaces de grabaciones cuando el curso está Abierto.",
			"Revisar grabaciones existentes en cursos Finalizados o Cerrados.",
		],
		rules: [
			"Solo se agregan o modifican grabaciones si el curso está Activo y Abierto.",
			"Finalizado o Cerrado permite listar, pero no agregar nuevos enlaces.",
			"Si el curso está inactivo, no tiene sentido mostrarlo en este módulo.",
		],
		support: [
			"Si el botón Agregar está deshabilitado, revisar si el curso está Abierto y activo.",
			"Si una grabación no aparece al estudiante, revisar si está publicada y si pertenece al run correcto.",
		],
	},
	{
		id: "certificados",
		title: "Certificados",
		icon: "ri-file-paper-2-fill",
		path: "/backoffice/talks",
		purpose: "Controla la habilitación, emisión y descarga de certificados por curso.",
		actions: [
			"Revisar estado del curso y si la emisión está habilitada.",
			"Modificar estado entre Abierto, Finalizado y Cerrado según ciclo académico.",
			"Habilitar o deshabilitar emisión cuando el curso está Finalizado o Cerrado.",
			"Descargar certificados individuales de estudiantes aprobados.",
		],
		rules: [
			"Un certificado individual requiere curso Finalizado o Cerrado, emisión habilitada, estudiante Completado y nota >= 4.0.",
			"El envío masivo no se permite cuando el curso está Cerrado.",
			"Cancelado significa que la matrícula fue dada de baja y no genera certificado.",
			"Reprobado no genera certificado aunque el curso tenga emisión habilitada.",
		],
		support: [
			"Si no aparece el botón de descarga, revisar estado del curso, emisión, estado de matrícula y nota final.",
			"Si el estudiante aprobó pero no descarga, validar que esté Completado y que la nota esté guardada como 4.0 o superior.",
			"Si el curso está Abierto, primero debe finalizarse antes de descargar certificados.",
		],
	},
	{
		id: "noticias",
		title: "Noticias",
		icon: "ri-article-fill",
		path: "/backoffice/news",
		purpose: "Administra publicaciones visibles en la página pública.",
		actions: [
			"Crear noticias con título, contenido, imagen y estado de publicación.",
			"Editar, revisar o eliminar noticias existentes.",
			"Controlar qué contenido se muestra públicamente.",
		],
		rules: [
			"Solo las noticias publicadas deben mostrarse en la web pública.",
			"Las imágenes y textos deben ser revisados antes de publicar.",
		],
		support: [
			"Si una noticia no aparece, revisar estado de publicación, fecha y datos obligatorios.",
			"Si una imagen no carga, validar archivo, ruta y formato soportado.",
		],
	},
	{
		id: "carrusel",
		title: "Carrusel",
		icon: "ri-slideshow-2-fill",
		path: "/backoffice/carousel",
		purpose: "Gestiona los elementos destacados del inicio público.",
		actions: [
			"Crear elementos con título, subtítulo, imagen y enlace de destino.",
			"Ordenar o actualizar elementos según campañas o información destacada.",
			"Activar o desactivar elementos según necesidad.",
		],
		rules: [
			"Cada elemento debe tener contenido claro y una imagen adecuada.",
			"El enlace debe dirigir a una ruta válida o recurso externo confiable.",
		],
		support: [
			"Si un botón del carrusel no funciona, revisar el enlace configurado.",
			"Si el carrusel se ve vacío, validar que existan elementos activos.",
		],
	},
	{
		id: "textos",
		title: "Textos y contacto",
		icon: "ri-settings-3-fill",
		path: "/backoffice/web-config",
		purpose: "Mantiene los textos institucionales, misión, visión, contacto y redes de la página pública.",
		actions: [
			"Actualizar misión, visión y textos públicos.",
			"Editar datos de contacto y enlaces de redes sociales.",
			"Guardar cambios para reflejarlos en la página pública.",
		],
		rules: [
			"Los textos públicos deben mantenerse consistentes con la identidad de Somnus.",
			"Los enlaces externos deben ser revisados antes de publicarse.",
		],
		support: [
			"Si un texto no cambia en la web pública, revisar si se guardó correctamente y refrescar la página.",
			"Si una red social no abre, validar que el enlace comience con http:// o https://.",
		],
	},
];

const moduleProcedures = {
	panel: [
		{
			title: "Revisar el estado general",
			icon: "ri-dashboard-fill",
			steps: [
				"Ingresar al panel administrativo con un usuario Admin activo.",
				"Revisar los indicadores visibles y detectar si existe algun valor fuera de lo esperado.",
				"Entrar al módulo relacionado cuando un indicador requiera revisión.",
				"Confirmar que el usuario conectado corresponda al administrador que está operando.",
			],
			notes: ["El panel es una vista de orientación; la gestión detallada se realiza en cada módulo."],
		},
		{
			title: "Cambiar de módulo",
			icon: "ri-route-fill",
			steps: [
				"Usar el menú lateral izquierdo.",
				"Seleccionar el módulo requerido según la consulta o tarea.",
				"Verificar que el título de la pantalla coincida con la acción que se quiere ejecutar.",
			],
		},
	],
	organizaciones: [
		...tableProcedures,
		{
			title: "Crear una organización",
			icon: "ri-building-add-fill",
			steps: [
				"Entrar a Organizaciones.",
				"Presionar el botón Nueva organización.",
				"Completar nombre, RUT, datos de contacto y estado.",
				"Guardar el formulario.",
				"Buscar la organización creada y abrir su ficha para confirmar que los datos quedaron correctos.",
			],
			notes: ["El RUT de la organización debe quedar completo porque aparece en fichas y certificados."],
		},
		{
			title: "Revisar una organización",
			icon: "ri-eye-fill",
			steps: [
				"Buscar la organización por nombre o RUT.",
				"Presionar el botón de ver detalle.",
				"Revisar ficha, representantes, cursos asignados, lotes y estudiantes asociados.",
				"Validar que los cursos disponibles correspondan a contratos vigentes y con cupo.",
			],
		},
		{
			title: "Modificar una organización",
			icon: "ri-pencil-fill",
			steps: [
				"Buscar la organización.",
				"Confirmar que la organización esté activa; si está desactivada, primero debe activarse.",
				"Presionar el botón de edición.",
				"Actualizar solo los campos necesarios.",
				"Guardar cambios.",
				"Volver a abrir la ficha para confirmar la información actualizada.",
			],
		},
		{
			title: "Desactivar o reactivar una organización",
			icon: "ri-delete-bin-fill",
			steps: [
				"Buscar la organización.",
				"Revisar si tiene estudiantes, representantes o contratos asociados.",
				"Usar desactivacion cuando se quiera bloquear operación conservando historial.",
				"Confirmar la desactivación solicitada por el sistema.",
				"Si se necesita operar nuevamente con la organización, usar Activar y confirmar la acción.",
				"Después de reactivar, revisar manualmente qué usuarios, representantes, contratos y cursos asociados deben volver a habilitarse.",
			],
			notes: [
				"Una organización desactivada no se elimina de la base de datos.",
				"Mientras esté desactivada, las acciones operativas deben permanecer visibles pero deshabilitadas.",
			],
		},
		{
			title: "Crear representante",
			icon: "ri-user-add-fill",
			steps: [
				"Abrir la ficha de una organización activa.",
				"Presionar Crear representante.",
				"Completar nombres, apellidos, correo electrónico y RUT.",
				"Guardar y revisar que el representante quede asociado a la organización.",
				"Confirmar que el acceso dependa del estado de la organización y de sus condiciones contractuales.",
			],
			notes: [
				"No se pueden crear ni reactivar representantes activos en organizaciones desactivadas.",
				"El RUT del representante debe ser válido y único; el correo y usuario también deben ser únicos.",
			],
		},
		{
			title: "Reactivar representante",
			icon: "ri-user-follow-fill",
			steps: [
				"Confirmar que la organización esté activa.",
				"Buscar el representante en la ficha de organización.",
				"Presionar Habilitar si la acción está disponible.",
				"Confirmar la acción solicitada por el sistema.",
			],
			notes: ["Si la organización está desactivada, primero debe activarse la organización."],
		},
		{
			title: "Asignar cursos o contratos",
			icon: "ri-bookmark-3-fill",
			steps: [
				"Abrir la ficha de la organización.",
				"Confirmar que la organización esté activa.",
				"Ir a la sección de cursos o contratos asociados.",
				"Seleccionar el curso, fechas de vigencia y cupos.",
				"Guardar la asignacion.",
				"Entrar con representante o revisar la ficha para confirmar que el curso aparezca disponible o histórico según regla.",
			],
			notes: [
				"El selector muestra solo cursos activos con postulaciones vigentes.",
				"La fecha de inicio y término del curso es un dato académico separado de la ventana de postulación.",
				"Para nuevas matrículas el curso debe estar Abierto, activo, no vencido y con cupo; no se exige que haya llegado la fecha de inicio.",
				"Los estudiantes matriculados antes del inicio quedan preinscritos y acceden al aula recién desde la fecha de inicio del curso.",
				"Después de la fecha de término no se permiten nuevas matrículas, pero los estudiantes ya inscritos conservan acceso durante 30 días de gracia.",
				"Deshabilitar un periodo deshabilita sus cursos asignados activos.",
			],
		},
	],
	usuarios: [
		...tableProcedures,
		{
			title: "Crear usuario",
			icon: "ri-user-add-fill",
			steps: [
				"Entrar a Usuarios.",
				"Presionar Nuevo usuario.",
				"Seleccionar rol Admin o Docente.",
				"Completar nombre, RUT, correo y estado.",
				"Guardar y validar que el usuario pueda verse en el listado.",
			],
			notes: [
				"Representantes se crean desde la ficha de organización.",
				"Estudiantes se crean principalmente desde cargas o flujos de matrícula.",
			],
		},
		{
			title: "Revisar ficha de usuario",
			icon: "ri-account-box-fill",
			steps: [
				"Buscar el usuario por nombre, RUT, correo o nombre de usuario.",
				"Presionar el botón de ver detalle.",
				"Revisar datos personales, rol, estado y organización asociada.",
				"En estudiantes, revisar historial académico, cursos y certificados.",
			],
		},
		{
			title: "Modificar usuario",
			icon: "ri-edit-2-fill",
			steps: [
				"Buscar el usuario.",
				"Confirmar que el usuario esté activo; si está desactivado, primero debe reactivarse.",
				"Presionar editar.",
				"Actualizar datos permitidos por el formulario.",
				"Guardar cambios.",
				"Confirmar en la ficha que rol, estado y organización quedaron correctos.",
			],
		},
		{
			title: "Resetear contraseña",
			icon: "ri-key-2-fill",
			steps: [
				"Buscar el usuario afectado.",
				"Confirmar que el usuario esté activo.",
				"Abrir la acción de cambio o reseteo de contraseña si está disponible.",
				"Ingresar la nueva contraseña temporal.",
				"Guardar y comunicar la nueva credencial por un canal seguro.",
			],
			notes: ["En entorno local de prueba puede usarse una clave simple; en produccion no debe hacerse."],
		},
		{
			title: "Desactivar o reactivar usuario",
			icon: "ri-user-unfollow-fill",
			steps: [
				"Buscar el usuario.",
				"Revisar si tiene cursos, matrículas, certificados o acciones historicas.",
				"Desactivar si se quiere bloquear acceso conservando historial.",
				"Confirmar la acción en el mensaje del sistema.",
				"Para reactivar, verificar que su organización asociada esté activa cuando aplique.",
				"Si pertenece a una organización desactivada, primero debe activarse la organización.",
			],
			notes: [
				"Desactivar usuario no afecta a su organización.",
				"No se puede desactivar el usuario máximo del sistema ni la propia cuenta que opera el panel.",
			],
		},
	],
	cursos: [
		...tableProcedures,
		{
			title: "Crear curso",
			icon: "ri-book-mark-fill",
			steps: [
				"Entrar a Cursos.",
				"Presionar Nuevo curso.",
				"Completar código, nombre, descripción, imagen, precio, modalidad y duración.",
				"Definir fechas, horario, docente, cupos y datos académicos obligatorios.",
				"Si la modalidad es asincrónica, no definir horario semanal.",
				"Validar que las fechas de postulación no superen el inicio del curso.",
				"Guardar y revisar que el curso aparezca en el listado.",
			],
			notes: ["El backend rechaza cursos activos con datos operativos incompletos o fechas incoherentes."],
		},
		{
			title: "Revisar curso",
			icon: "ri-eye-fill",
			steps: [
				"Buscar el curso por código o nombre.",
				"Presionar ver detalle.",
				"Revisar información general, temario, contenidos y reglas de certificación.",
				"Si se necesita revisar estudiantes, hacerlo desde la organización/asignación o run operativo correspondiente.",
			],
			notes: ["Una plantilla puede estar asignada a varias organizaciones; el catálogo no debe mezclar estudiantes de distintos clientes."],
		},
		{
			title: "Modificar curso",
			icon: "ri-pencil-fill",
			steps: [
				"Buscar el curso.",
				"Confirmar que el curso de catálogo esté activo; si está inactivo, primero debe activarse.",
				"Presionar editar.",
				"Actualizar datos académicos, docente, fechas, cupos o información pública permitida.",
				"Confirmar que el docente seleccionado esté activo y que las fechas mantengan coherencia.",
				"Guardar cambios.",
				"Revisar el detalle para confirmar que la información quedo coherente.",
			],
			notes: ["La gestión de estado del curso y emisión de certificados se realiza en Certificados."],
		},
		{
			title: "Desactivar o reactivar curso",
			icon: "ri-delete-bin-6-fill",
			steps: [
				"Buscar el curso.",
				"Revisar si tiene estudiantes, certificados o historial asociado.",
				"Usar Desactivar cuando se quiera retirar el curso de operación conservando historial.",
				"Confirmar en la tabla que no existan organizaciones con el curso En curso o Finalizado.",
				"Confirmar la desactivación solicitada por el sistema.",
				"Si se necesita volver a usar el curso, activar el catálogo y revisar manualmente sus asignaciones a organizaciones.",
			],
			notes: [
				"No se elimina fisicamente el curso de la base de datos.",
				"Las ediciones, matrículas y certificados se conservan para auditoría.",
				"Reactivar el catálogo no reactiva automáticamente asignaciones de empresa desactivadas.",
			],
		},
	],
	clases: [
		...tableProcedures,
		{
			title: "Agregar grabacion",
			icon: "ri-add-circle-fill",
			steps: [
				"Entrar a Clases grabadas.",
				"Buscar el curso.",
				"Confirmar que el curso esté activo y Abierto.",
				"Presionar Agregar.",
				"Completar título, enlace y datos solicitados.",
				"Guardar y revisar que la grabacion aparezca asociada al curso.",
			],
			notes: ["Si el curso está Finalizado o Cerrado, el botón Agregar debe estar deshabilitado."],
		},
		{
			title: "Revisar grabaciones",
			icon: "ri-play-list-2-fill",
			steps: [
				"Buscar el curso.",
				"Presionar ver grabaciones.",
				"Revisar cantidad, título, enlace y estado de publicacion.",
				"Abrir el enlace para validar que apunte al recurso correcto.",
			],
		},
		{
			title: "Editar grabacion",
			icon: "ri-edit-box-fill",
			steps: [
				"Abrir las grabaciones del curso.",
				"Presionar editar sobre la grabacion requerida.",
				"Actualizar título, enlace o estado.",
				"Guardar cambios.",
				"Validar que el enlace actualizado funcione.",
			],
			notes: ["La edición solo debe permitirse cuando el curso está activo y Abierto."],
		},
		{
			title: "Eliminar grabacion",
			icon: "ri-delete-bin-fill",
			steps: [
				"Abrir las grabaciones del curso.",
				"Identificar la grabacion que se quiere retirar.",
				"Presionar eliminar.",
				"Confirmar la acción.",
				"Revisar que ya no aparezca en el listado.",
			],
		},
	],
	certificados: [
		...tableProcedures,
		{
			title: "Cambiar estado del curso",
			icon: "ri-arrow-left-right-fill",
			steps: [
				"Entrar a Certificados.",
				"Ubicar el curso.",
				"Seleccionar Abierto, Finalizado o Cerrado en la columna Estado.",
				"Si se cambia a Abierto, la emisión se apaga automáticamente.",
				"Revisar el aviso Guardar cambios en la cabecera.",
				"Presionar el botón de ticket para guardar o el tacho para deshacer.",
			],
		},
		{
			title: "Habilitar emisión",
			icon: "ri-toggle-fill",
			steps: [
				"Entrar a Certificados.",
				"Confirmar que el curso esté Finalizado o Cerrado.",
				"Activar el switch de Emision habilitada.",
				"Guardar los cambios con el botón de ticket.",
				"Revisar que las acciones de certificados queden disponibles según regla.",
			],
			notes: ["En cursos Abiertos el switch debe permanecer apagado y deshabilitado."],
		},
		{
			title: "Descargar certificado individual",
			icon: "ri-download-2-fill",
			steps: [
				"Entrar a Certificados.",
				"Abrir las acciones del curso.",
				"Buscar al estudiante.",
				"Confirmar que esté Completado y tenga nota 4.0 o superior.",
				"Presionar el botón de descarga en la fila del estudiante.",
			],
			notes: ["No se emite certificado a matrículas Canceladas o Reprobadas."],
		},
		{
			title: "Enviar certificados masivamente",
			icon: "ri-mail-send-fill",
			steps: [
				"Entrar a Certificados.",
				"Confirmar que el curso esté Finalizado y con emisión habilitada.",
				"Abrir las acciones del curso.",
				"Revisar estudiantes aprobados disponibles para emisión.",
				"Ejecutar el envío masivo si corresponde.",
			],
			notes: ["El envío masivo no se permite cuando el curso está Cerrado."],
		},
	],
	noticias: [
		...tableProcedures,
		{
			title: "Crear noticia",
			icon: "ri-newspaper-fill",
			steps: [
				"Entrar a Noticias.",
				"Presionar Nueva noticia.",
				"Completar título, contenido, imagen y estado de publicacion.",
				"Guardar la noticia.",
				"Revisar la página pública si quedo publicada.",
			],
		},
		{
			title: "Modificar noticia",
			icon: "ri-edit-fill",
			steps: [
				"Buscar la noticia.",
				"Presionar editar.",
				"Actualizar texto, imagen o estado.",
				"Guardar cambios.",
				"Validar el resultado en el listado y, si aplica, en la página pública.",
			],
		},
		{
			title: "Eliminar noticia",
			icon: "ri-delete-bin-fill",
			steps: [
				"Buscar la noticia.",
				"Presionar eliminar.",
				"Confirmar la acción.",
				"Revisar que ya no aparezca en el listado.",
			],
		},
	],
	carrusel: [
		...tableProcedures,
		{
			title: "Crear elemento",
			icon: "ri-slideshow-fill",
			steps: [
				"Entrar a Carrusel.",
				"Presionar Nuevo elemento.",
				"Completar título, subtitulo, imagen, enlace y orden si corresponde.",
				"Guardar.",
				"Validar que el elemento se vea correctamente en la página pública.",
			],
		},
		{
			title: "Modificar elemento",
			icon: "ri-edit-circle-fill",
			steps: [
				"Buscar el elemento.",
				"Presionar editar.",
				"Actualizar texto, imagen, enlace, orden o estado.",
				"Guardar cambios.",
				"Revisar el carrusel publico.",
			],
		},
		{
			title: "Eliminar elemento",
			icon: "ri-delete-bin-fill",
			steps: [
				"Buscar el elemento.",
				"Presionar eliminar.",
				"Confirmar la acción.",
				"Validar que se retire del listado y de la página pública.",
			],
		},
	],
	textos: [
		{
			title: "Actualizar textos institucionales",
			icon: "ri-file-text-fill",
			steps: [
				"Entrar a Textos y contacto.",
				"Ubicar la sección de mision, vision o texto institucional.",
				"Editar el contenido necesario.",
				"Guardar cambios.",
				"Revisar la página pública para confirmar que el texto se muestre correctamente.",
			],
		},
		{
			title: "Actualizar contacto y redes",
			icon: "ri-contacts-book-fill",
			steps: [
				"Entrar a Textos y contacto.",
				"Actualizar correo, telefono, direccion o enlaces de redes sociales.",
				"Verificar que cada enlace use una URL valida.",
				"Guardar cambios.",
				"Probar los enlaces desde la página pública.",
			],
		},
	],
};

const guideModules = modules.map((module) => ({
	...module,
	procedures: moduleProcedures[module.id] || [],
}));

const GUIDE_SECTION_IDS = [
	"glosario",
	"permisos",
	"casos",
	"reglas",
	"decisiones",
	"ciclos",
	"errores",
	"columnas",
	"evitar",
	"flujos",
];

const ROLE_GUIDE_CONFIG = {
	admin: {
		label: "Administrador",
		modules: guideModules.map((module) => module.id),
		sections: GUIDE_SECTION_IDS,
		overview:
			"El panel admin controla la operación central de Somnus: organizaciones, usuarios, cursos, grabaciones, certificados y contenido publico. La regla principal es distinguir entre estado del catálogo, estado del run y estado de matrícula.",
		description:
			"Documentación completa para administrar el sistema, resolver dudas operativas y revisar reglas de negocio.",
		canOpenModules: true,
	},
	teacher: {
		label: "Docente",
		modules: ["cursos", "clases", "certificados"],
		sections: ["glosario", "casos", "reglas", "decisiones", "ciclos", "errores", "columnas", "evitar", "flujos"],
		overview:
			"La guía docente se concentra en acceso a cursos asignados, gestión academica en cursos Abiertos, consulta en cursos Finalizados y restricciones cuando un curso está Cerrado.",
		description:
			"Documentación adaptada al rol docente, enfocada en contenidos, evaluaciones, estudiantes y reglas de acceso académico.",
		canOpenModules: false,
	},
	company_admin: {
		label: "Representante",
		modules: ["organizaciones", "usuarios", "cursos", "certificados"],
		sections: ["glosario", "casos", "decisiones", "ciclos", "errores", "columnas", "flujos"],
		overview:
			"La guía del representante se enfoca en cursos asociados a la organización, disponibilidad, cupos, matrículas, cargas de estudiantes e historial.",
		description:
			"Documentación adaptada al representante de empresa, enfocada en matrículas, contratos y seguimiento de estudiantes.",
		canOpenModules: false,
	},
	student: {
		label: "Estudiante",
		modules: ["cursos", "certificados"],
		sections: ["glosario", "casos", "ciclos", "errores", "columnas", "flujos"],
		overview:
			"La guía estudiante explica acceso al aula, avance, evaluaciones, notas, estados de matrícula y condiciones para certificados.",
		description:
			"Documentación adaptada al estudiante, enfocada en aula virtual, evaluación, aprobación y certificados.",
		canOpenModules: false,
	},
};

function normalizeGuideRole(role) {
	const value = String(role || "").trim().toLowerCase();

	if (value === "admin" || value === "administrator") return "admin";
	if (value === "teacher" || value === "docente") return "teacher";
	if (value === "companyadmin" || value === "company_admin" || value === "representante") {
		return "company_admin";
	}
	if (value === "student" || value === "estudiante") return "student";

	return "admin";
}

function getGuideConfig(role) {
	switch (role) {
		case "teacher":
			return ROLE_GUIDE_CONFIG.teacher;
		case "company_admin":
			return ROLE_GUIDE_CONFIG.company_admin;
		case "student":
			return ROLE_GUIDE_CONFIG.student;
		case "admin":
		default:
			return ROLE_GUIDE_CONFIG.admin;
	}
}

const workflows = [
	{
		title: "Crear una empresa y dejarla lista para matricular",
		steps: [
			"Crear la organización en Organizaciones.",
			"Crear o asociar su representante.",
			"Asignar cursos/contratos con fechas y cupos.",
			"Entregar la plantilla de carga o solicitar que el representante cargue estudiantes.",
			"Matricular estudiantes solo en cursos disponibles.",
		],
	},
	{
		title: "Preparar un curso para ejecución",
		steps: [
			"Crear el curso y completar información general.",
			"Asignar docente, fechas, modalidad, cupos y horario.",
			"Confirmar que el estado del run esté Abierto.",
			"Cargar o revisar contenido académico.",
			"Verificar que los estudiantes queden matriculados en el run correcto.",
		],
	},
	{
		title: "Cerrar ciclo académico y emitir certificados",
		steps: [
			"Revisar que los estudiantes hayan rendido examen.",
			"Confirmar notas finales y estados Completado/Reprobado/Cancelado.",
			"Pasar el curso a Finalizado.",
			"Habilitar emisión de certificados si corresponde.",
			"Descargar certificados individuales o realizar envío masivo antes de cerrar.",
			"Pasar a Cerrado cuando el proceso esté terminado.",
		],
	},
	{
		title: "Validar por que un estudiante no accede al aula",
		steps: [
			"Confirmar que el usuario estudiante esté activo.",
			"Revisar que tenga matrícula en el curso correcto.",
			"Validar que la matrícula no esté Cancelada.",
			"Confirmar que el curso no esté Cerrado.",
			"Revisar el periodo de acceso del curso y la organización asociada.",
			"Si rindió examen recientemente, confirmar que la matrícula siga asociada al mismo run.",
		],
	},
	{
		title: "Validar por que no se puede emitir un certificado",
		steps: [
			"Confirmar que el curso esté Finalizado o Cerrado.",
			"Revisar que la emisión esté habilitada.",
			"Confirmar que el estudiante esté Completado.",
			"Validar que la nota final sea 4.0 o superior.",
			"Descartar matrículas Canceladas o Reprobadas.",
			"Para envío masivo, confirmar que el curso no esté Cerrado.",
		],
	},
];

const glossaryTerms = [
	{
		term: "Activo / Inactivo",
		meaning: "Estado operacional del registro. Si un curso u organización está inactivo, no debe operar normalmente.",
		example: "Un curso inactivo no deberia aparecer en Clases grabadas porque no tiene sentido gestionarlo.",
	},
	{
		term: "Abierto",
		meaning: "Estado del run que permite operación completa: matrículas, avance, contenidos, grabaciones y evaluación.",
		example: "Un docente puede editar contenidos solo si el curso está Abierto.",
	},
	{
		term: "Finalizado",
		meaning: "Estado del run usado cuando término la ejecucion academica. Permite consulta y certificados, pero bloquea cambios operativos.",
		example: "Un curso Finalizado puede habilitar emisión de certificados.",
	},
	{
		term: "Cerrado",
		meaning: "Estado histórico final. Admin y representante pueden consultar historial; docente y estudiante no acceden al curso.",
		example: "Un certificado individual puede descargarse si el curso Cerrado tiene emisión habilitada.",
	},
	{
		term: "Disponible / No disponible",
		meaning: "Resultado visible para representantes según contrato, cupo, fechas, actividad y estado del run.",
		example: "Un contrato vencido puede mostrarse como No disponible para historial.",
	},
	{
		term: "Completado",
		meaning: "Estado de matrícula del estudiante cuando aprobo el curso.",
		example: "Completado con nota 4.0 o superior permite certificado si el curso cumple reglas.",
	},
	{
		term: "Reprobado",
		meaning: "Estado de matrícula cuando la nota final es menor a 4.0.",
		example: "Reprobado no genera certificado aunque el curso tenga emisión habilitada.",
	},
	{
		term: "Cancelado",
		meaning: "Estado de matrícula dado de baja. No representa aprobación ni reprobacion academica.",
		example: "Cancelado no puede rendir ni descargar certificado.",
	},
];

const permissionMatrix = [
	{
		role: "Admin",
		view: "Todos los módulos administrativos.",
		create: "Organizaciones, usuarios, cursos, noticias, carrusel, grabaciones y configuraciones.",
		edit: "Datos administrativos, cursos, estados, emisión, contenidos públicos y usuarios.",
		restrictions: "Debe respetar reglas de estado, jerarquía e inactividad; un registro inactivo solo puede reactivarse.",
	},
	{
		role: "Docente",
		view: "Cursos asignados Abiertos o Finalizados.",
		create: "Contenidos, exámenes, mensajes o recursos solo cuando el curso está Abierto.",
		edit: "Material académico y evaluaciones solo en cursos Abiertos.",
		restrictions: "No accede a cursos Cerrados y no modifica cursos Finalizados.",
	},
	{
		role: "Representante",
		view: "Cursos y estudiantes asociados a su organización.",
		create: "Carga o matrícula estudiantes según contrato vigente.",
		edit: "Datos permitidos de su organización o estudiantes según pantalla.",
		restrictions: "Solo opera si su usuario y organización están activos; solo matrícula si el curso está Abierto, activo, vigente y con cupo.",
	},
	{
		role: "Estudiante",
		view: "Cursos donde tiene matrícula vigente o historial permitido.",
		create: "No crea registros administrativos.",
		edit: "Su avance académico y respuestas de evaluación dentro de las reglas del aula.",
		restrictions: "No accede a cursos Cerrados ni a matrículas Canceladas.",
	},
];

const supportCases = [
	{
		question: "Por qué una acción aparece deshabilitada?",
		answer: "Revisar si la organización o usuario está desactivado. Un registro inactivo solo permite activar/reactivar; las demás acciones quedan bloqueadas hasta reactivarlo.",
	},
	{
		question: "Por qué no se puede reactivar un usuario?",
		answer: "Si el usuario pertenece a una organización desactivada, primero debe activarse la organización y luego revisar si corresponde reactivar el usuario.",
	},
	{
		question: "Por qué un representante no puede matricular estudiantes?",
		answer: "Revisar que el representante y la organización estén activos, además de contrato activo, cupo disponible, fechas vigentes, curso activo y run en estado Abierto.",
	},
	{
		question: "Por qué un estudiante ve el mensaje No estás matriculado?",
		answer: "Validar que la matrícula pertenezca al mismo run, que no esté Cancelada y que el curso no esté Cerrado.",
	},
	{
		question: "Por qué el docente no puede modificar contenidos?",
		answer: "La edición docente solo corresponde si el curso está Abierto y el docente está asignado al run.",
	},
	{
		question: "Por qué no aparece el botón de certificado?",
		answer: "El curso debe estar Finalizado o Cerrado, con emisión habilitada, y el estudiante debe estar Completado con nota >= 4.0.",
	},
	{
		question: "Qué significa Cancelado en estudiantes?",
		answer: "La matrícula fue dada de baja. No es una reprobacion, pero bloquea aula, nota final y certificado.",
	},
	{
		question: "Por qué dos empresas ven los mismos cursos?",
		answer: "Puede ocurrir si ambas tienen contratos asociados a los mismos cursos, fechas y cupos. Revisar la ficha de cada organización.",
	},
];

const criticalBusinessRules = [
	"Un registro inactivo queda en modo histórico/reactivable; la única acción permitida es activar/reactivar.",
	"Las acciones no permitidas sobre registros inactivos deben verse deshabilitadas y estar bloqueadas también por backend.",
	"Desactivar una organización desactiva logicamente usuarios asociados, representantes, contratos marco y asignaciones de curso activas, pero no desactiva plantillas del catálogo.",
	"Reactivar una organización no reactiva automáticamente sus usuarios, representantes, contratos ni cursos asignados.",
	"No se puede reactivar un usuario asociado a una organización desactivada.",
	"No se puede desactivar el usuario máximo del sistema ni la propia cuenta que opera el panel.",
	"Solo el run Abierto permite nuevas matrículas.",
	"Solo el run Abierto permite edición docente, reset de exámenes y nuevas grabaciones.",
	"Run Finalizado permite consulta y emisión, pero no cambios académicos.",
	"Run Cerrado bloquea acceso de docentes y estudiantes, pero conserva historial administrativo.",
	"Certificado individual requiere curso Finalizado o Cerrado, emisión habilitada, matrícula Completada y nota >= 4.0.",
	"Envio masivo de certificados requiere curso Finalizado y emisión habilitada; no se permite en Cerrado.",
	"Nota final aprobatoria es desde 4.0; bajo 4.0 corresponde Reprobado.",
	"Matricula Cancelada no puede generar certificado.",
	"Representante solo opera sobre cursos asociados a su organización.",
	"Representante solo opera si su usuario y su organización están activos.",
	"El estado Activo del catálogo no reemplaza el estado Abierto/Finalizado/Cerrado del run.",
];

const commonIssues = [
	{
		problem: "El curso aparece, pero no permite matricular.",
		cause: "Organización/representante inactivo, curso no Abierto, contrato vencido, sin cupos o inactivo.",
		where: "Organizaciones y Certificados.",
		action: "Revisar estado de organización/representante, contrato, cupos, fechas, estado del run y actividad del curso.",
	},
	{
		problem: "No se puede editar una organización o usuario.",
		cause: "El registro está desactivado.",
		where: "Organizaciones o Usuarios.",
		action: "Activar/reactivar primero; luego volver a ejecutar la acción permitida.",
	},
	{
		problem: "No se puede reactivar un usuario.",
		cause: "La organización asociada está desactivada.",
		where: "Usuarios y Organizaciones.",
		action: "Activar primero la organización; luego decidir si corresponde reactivar el usuario.",
	},
	{
		problem: "No se puede descargar certificado individual.",
		cause: "Curso sin emisión, estudiante no aprobado o matrícula Cancelada/Reprobada.",
		where: "Certificados y detalle del curso.",
		action: "Validar estado Finalizado/Cerrado, emisión habilitada, nota y estado Completado.",
	},
	{
		problem: "Docente ve estudiantes mezclados.",
		cause: "Consulta consolidada por plantilla en vez de run especifico.",
		where: "Portal docente y detalle del curso.",
		action: "Confirmar que el listado esté filtrado por run, empresa y periodo correcto.",
	},
	{
		problem: "Estudiante queda fuera del aula despues del examen.",
		cause: "Cambio de estado o pérdida de asociacion de matrícula al run.",
		where: "Matricula, intentos de examen y aula.",
		action: "Validar que la matrícula siga activa/completada en el mismo curso y run.",
	},
	{
		problem: "Grabaciones no permiten agregar enlaces.",
		cause: "Curso Finalizado, Cerrado o inactivo.",
		where: "Clases grabadas.",
		action: "Solo agregar si el curso está activo y Abierto.",
	},
	{
		problem: "Texto publico no se actualiza.",
		cause: "Cambio no guardado, cache o dato incompleto.",
		where: "Textos y contacto, Noticias o Carrusel.",
		action: "Guardar, recargar la página pública y validar estado activo/publicado.",
	},
];

const columnDictionary = [
	{ column: "Codigo", meaning: "Identificador corto del curso o registro.", modules: "Cursos, Certificados, Clases grabadas, Organizaciones." },
	{ column: "Curso", meaning: "Nombre del curso o run mostrado en pantalla.", modules: "Cursos, Certificados, Clases grabadas." },
	{ column: "Estado", meaning: "Puede representar estado del run, matrícula o registro según módulo.", modules: "Certificados, Cursos, Usuarios, Clases grabadas." },
	{ column: "Emision habilitada", meaning: "Indica si el curso permite generar certificados.", modules: "Certificados." },
	{ column: "Nota Final", meaning: "Nota calculada en escala 1.0 a 7.0 a partir del puntaje de evaluación.", modules: "Cursos, Certificados, Usuarios." },
	{ column: "Certificado", meaning: "Accion de descarga o indicador de disponibilidad del certificado.", modules: "Cursos, Certificados." },
	{ column: "Cupo", meaning: "Relacion entre estudiantes inscritos y cupos disponibles.", modules: "Organizaciones, Cursos." },
	{ column: "Motivo", meaning: "Explica por que un curso está disponible o no disponible.", modules: "Portal representante." },
	{ column: "Grabaciones", meaning: "Cantidad de enlaces asociados al curso.", modules: "Clases grabadas." },
	{ column: "Activo / Activa", meaning: "Estado operacional del registro. En las tablas administrativas debe ubicarse despues de las columnas informativas y justo antes de Acciones.", modules: "Modulos administrativos." },
	{ column: "Acciones", meaning: "Botones para ver, crear, editar, desactivar, reactivar, descargar o ejecutar operaciones. Si el registro está inactivo, las acciones operativas deben verse deshabilitadas.", modules: "Modulos administrativos." },
];

const decisionIndex = [
	{
		situation: "No descarga certificado",
		check: "Estado del curso, emisión habilitada, nota final, estado de matrícula y tipo de descarga.",
		goTo: "Certificados, detalle del curso y ficha del estudiante.",
	},
	{
		situation: "No puede matricular",
		check: "Organización activa, representante activo, contrato activo, cupo, vigencia, curso activo y run Abierto.",
		goTo: "Organizaciones y contratos asociados.",
	},
	{
		situation: "Acción deshabilitada",
		check: "Estado activo/inactivo del registro y regla jerárquica asociada.",
		goTo: "Organizaciones, Usuarios y reglas críticas.",
	},
	{
		situation: "No puede reactivar usuario",
		check: "Si el usuario pertenece a una organización desactivada.",
		goTo: "Primero activar organización; luego volver a Usuarios.",
	},
	{
		situation: "Docente no puede editar",
		check: "Docente asignado, curso Abierto y permisos del portal docente.",
		goTo: "Cursos, portal docente y reglas críticas.",
	},
	{
		situation: "Estudiante no accede al aula",
		check: "Usuario activo, matrícula vigente, curso no Cerrado, periodo de acceso y run correcto.",
		goTo: "Usuarios, Cursos y aula del estudiante.",
	},
	{
		situation: "Grabaciones no aceptan enlaces",
		check: "Curso activo y estado Abierto.",
		goTo: "Clases grabadas y Certificados.",
	},
	{
		situation: "Representante ve cursos historicos",
		check: "Contrato vencido, run Finalizado/Cerrado o curso no disponible por cupo/fecha.",
		goTo: "Portal representante y ficha de organización.",
	},
];

const courseLifecycle = [
	{
		state: "Abierto",
		allowed: "Matricular, editar contenido, agregar grabaciones, rendir examen y gestionar avance.",
		blocked: "Descargar certificados finales.",
		next: "Finalizado",
	},
	{
		state: "Finalizado",
		allowed: "Consultar resultados, revisar estudiantes, habilitar emisión y descargar certificados individuales.",
		blocked: "Nuevas matrículas, edición docente, reset de exámenes y nuevas grabaciones.",
		next: "Cerrado",
	},
	{
		state: "Cerrado",
		allowed: "Consultar historial administrativo y descargar certificados individuales si emisión está habilitada.",
		blocked: "Acceso docente, acceso estudiante, envío masivo y cambios académicos.",
		next: "Reabrir solo si administración decide volver a operar el run.",
	},
];

const studentLifecycle = [
	{
		state: "Matriculado / Activo",
		meaning: "El estudiante tiene una matrícula vigente y puede avanzar si el curso está Abierto.",
		next: "En curso o Cancelado.",
	},
	{
		state: "En curso",
		meaning: "El estudiante revisa contenidos, registra avance y rinde evaluación cuando cumple requisitos.",
		next: "Completado o Reprobado.",
	},
	{
		state: "Completado",
		meaning: "Aprobo con nota 4.0 o superior.",
		next: "Certificado si el curso cumple reglas de emisión.",
	},
	{
		state: "Reprobado",
		meaning: "Obtuvo nota menor a 4.0.",
		next: "No genera certificado.",
	},
	{
		state: "Cancelado",
		meaning: "La matrícula fue dada de baja por una decisión administrativa u operativa.",
		next: "No accede al aula ni genera certificado.",
	},
];

const doNotRules = [
	"No cerrar un curso antes de realizar el envío masivo de certificados si se necesita usar esa funcion.",
	"No usar Cancelado para estudiantes que reprobaron; Reprobado depende de la nota final.",
	"No eliminar organizaciones o usuarios con historial; usar desactivación lógica y conservar trazabilidad.",
	"No cambiar un curso a Abierto solo para descargar certificados.",
	"No asignar el mismo contrato a varias empresas sin confirmar que corresponde comercialmente.",
	"No editar contenido docente cuando el curso ya está Finalizado o Cerrado.",
	"No habilitar emisión si las notas finales no fueron revisadas.",
	"No cargar grabaciones en cursos Finalizados o Cerrados.",
	"No ejecutar acciones operativas sobre registros inactivos; primero deben reactivarse.",
];

function SectionList({ title, items }) {
	return (
		<div className="guide20-section-list">
			<h4>{title}</h4>
			<ul>
				{items.map((item) => (
					<li key={item}>{item}</li>
				))}
			</ul>
		</div>
	);
}

function ProcedureExplorer({ module }) {
	const procedures = module.procedures || [];
	const [activeProcedureIndex, setActiveProcedureIndex] = useState(0);
	const activeProcedure = procedures[Math.min(activeProcedureIndex, procedures.length - 1)];

	if (!activeProcedure) {
		return null;
	}
	
	return (
		<div className="guide20-procedures">
			<div className="guide20-procedure-heading">
				<div>
					<h4 className="">Consulta paso a paso</h4>
					<p>Guia por acción</p>
				</div>
				<span>{procedures.length} acciones documentadas</span>
			</div>

			<div className="guide20-procedure-layout">
				<div className="guide20-procedure-picker" aria-label={`Acciones de ${module.title}`}>
					{procedures.map((procedure, index) => (
						<button
							type="button"
							key={`${module.id}-${procedure.title}`}
							className={index === activeProcedureIndex ? "is-active" : ""}
							onClick={() => setActiveProcedureIndex(index)}
						>
							<i className={procedure.icon} aria-hidden />
							<span>{procedure.title}</span>
						</button>
					))}
				</div>

				<article className="guide20-procedure-detail">
					<div className="guide20-procedure-title">
						<i className={activeProcedure.icon} aria-hidden />
						<h4>{activeProcedure.title}</h4>
					</div>
					<ol>
						{activeProcedure.steps.map((step) => (
							<li key={step}>{step}</li>
						))}
					</ol>
					{activeProcedure.notes?.length > 0 && (
						<div className="guide20-procedure-notes">
							<strong>Consideraciones</strong>
							<ul>
								{activeProcedure.notes.map((note) => (
									<li key={note}>{note}</li>
								))}
							</ul>
						</div>
					)}
				</article>
			</div>
		</div>
	);
}

function buildSearchIndex(modulesForRole, sectionIds) {
	const sectionSet = new Set(sectionIds);
	const moduleEntries = modulesForRole.flatMap((module) => [
		{
			type: "Modulo",
			title: module.title,
			description: module.purpose,
			section: module.id,
		},
		...module.procedures.map((procedure) => ({
			type: "Paso a paso",
			title: `${module.title}: ${procedure.title}`,
			description: [...procedure.steps, ...(procedure.notes || [])].join(" "),
			section: module.id,
		})),
	]);

	return [
		...moduleEntries,
		...(sectionSet.has("glosario")
			? glossaryTerms.map((item) => ({
				type: "Glosario",
				title: item.term,
				description: `${item.meaning} ${item.example}`,
				section: "glosario",
			}))
			: []),
		...(sectionSet.has("casos")
			? supportCases.map((item) => ({
				type: "Caso frecuente",
				title: item.question,
				description: item.answer,
				section: "casos",
			}))
			: []),
		...(sectionSet.has("reglas")
			? criticalBusinessRules.map((rule) => ({
				type: "Regla critica",
				title: rule,
				description: rule,
				section: "reglas",
			}))
			: []),
		...(sectionSet.has("decisiones")
			? decisionIndex.map((item) => ({
				type: "Decision",
				title: item.situation,
				description: `${item.check} ${item.goTo}`,
				section: "decisiones",
			}))
			: []),
		...(sectionSet.has("errores")
			? commonIssues.map((issue) => ({
				type: "Error comun",
				title: issue.problem,
				description: `${issue.cause} ${issue.where} ${issue.action}`,
				section: "errores",
			}))
			: []),
		...(sectionSet.has("columnas")
			? columnDictionary.map((item) => ({
				type: "Columna",
				title: item.column,
				description: `${item.meaning} ${item.modules}`,
				section: "columnas",
			}))
			: []),
	];
}

export default function InstructivoAdminPro() {
	const navigate = useNavigate();
	const { activeRole, user } = useAuth();
	const [activeSection, setActiveSection] = useState("resumen");
	const [searchTerm, setSearchTerm] = useState("");
	const guideRole = normalizeGuideRole(activeRole || user?.role);
	const roleConfig = getGuideConfig(guideRole);
	const visibleModules = useMemo(
		() => guideModules.filter((module) => roleConfig.modules.includes(module.id)),
		[roleConfig],
	);
	const visibleSectionIds = roleConfig.sections;
	const activeModule = useMemo(
		() => visibleModules.find((module) => module.id === activeSection),
		[activeSection, visibleModules],
	);
	const allowedSections = useMemo(
		() => new Set(["resumen", ...visibleModules.map((module) => module.id), ...visibleSectionIds]),
		[visibleModules, visibleSectionIds],
	);
	const searchIndex = useMemo(
		() => buildSearchIndex(visibleModules, visibleSectionIds),
		[visibleModules, visibleSectionIds],
	);
	const searchResults = useMemo(() => {
		const query = searchTerm.trim().toLowerCase();

		if (!query) {
			return [];
		}

		return searchIndex
			.filter((entry) => `${entry.type} ${entry.title} ${entry.description}`.toLowerCase().includes(query))
			.slice(0, 12);
	}, [searchIndex, searchTerm]);

	const goToModule = (path) => {
		navigate(path);
	};

	const goToGuideSection = (section) => {
		setActiveSection(section);
		setSearchTerm("");
	};

	const canShowSection = (section) => visibleSectionIds.includes(section);

	useEffect(() => {
		if (!allowedSections.has(activeSection)) {
			setActiveSection("resumen");
		}
	}, [activeSection, allowedSections]);

	return (
		<div className="p-4 pt-5 backoffice-tables flex-grow-1 overflow-auto font-n">
			<header className="guide20-header">
				<div>
					<p className="guide20-eyebrow">Guia operativa Somnus</p>
					<h1>Instructivo</h1>
					<p>
						{roleConfig.description} Resume los módulos, flujos frecuentes y reglas de negocio
						que explican por que una acción está disponible o bloqueada.
					</p>
					<span className="guide20-role-chip">
						Guia adaptada para {roleConfig.label}
					</span>
					<div className="guide20-search">
						<i className="ri-search-line" aria-hidden />
						<input
							type="search"
							value={searchTerm}
							onChange={(event) => setSearchTerm(event.target.value)}
							placeholder="Buscar en el instructivo: certificado, cancelado, crear organización..."
							aria-label="Buscar en el instructivo"
						/>
						{searchTerm && (
							<button type="button" onClick={() => setSearchTerm("")} aria-label="Limpiar búsqueda">
								<i className="ri-close-fill" aria-hidden />
							</button>
						)}
					</div>
				</div>
			</header>

			<div className="guide20-layout">
				<aside className="guide20-nav" aria-label="Indice del instructivo">
					<button
						type="button"
						className={activeSection === "resumen" ? "is-active" : ""}
						onClick={() => setActiveSection("resumen")}
					>
						<i className="ri-file-list-3-fill" aria-hidden />
						<span>Resumen ejecutivo</span>
					</button>
					{visibleModules.map((module) => (
						<button
							type="button"
							key={module.id}
							className={activeSection === module.id ? "is-active" : ""}
							onClick={() => setActiveSection(module.id)}
						>
							<i className={module.icon} aria-hidden />
							<span>{module.title}</span>
						</button>
					))}
					{canShowSection("glosario") && (
						<button
							type="button"
							className={activeSection === "glosario" ? "is-active" : ""}
							onClick={() => setActiveSection("glosario")}
						>
							<i className="ri-book-3-fill" aria-hidden />
							<span>Glosario</span>
						</button>
					)}
					{canShowSection("permisos") && (
						<button
							type="button"
							className={activeSection === "permisos" ? "is-active" : ""}
							onClick={() => setActiveSection("permisos")}
						>
							<i className="ri-shield-user-fill" aria-hidden />
							<span>Permisos por rol</span>
						</button>
					)}
					{canShowSection("casos") && (
						<button
							type="button"
							className={activeSection === "casos" ? "is-active" : ""}
							onClick={() => setActiveSection("casos")}
						>
							<i className="ri-question-answer-fill" aria-hidden />
							<span>Casos frecuentes</span>
						</button>
					)}
					{canShowSection("reglas") && (
						<button
							type="button"
							className={activeSection === "reglas" ? "is-active" : ""}
							onClick={() => setActiveSection("reglas")}
						>
							<i className="ri-alert-fill" aria-hidden />
							<span>Reglas críticas</span>
						</button>
					)}
					{canShowSection("decisiones") && (
						<button
							type="button"
							className={activeSection === "decisiones" ? "is-active" : ""}
							onClick={() => setActiveSection("decisiones")}
						>
							<i className="ri-node-tree" aria-hidden />
							<span>Indice decisiones</span>
						</button>
					)}
					{canShowSection("ciclos") && (
						<button
							type="button"
							className={activeSection === "ciclos" ? "is-active" : ""}
							onClick={() => setActiveSection("ciclos")}
						>
							<i className="ri-loop-left-fill" aria-hidden />
							<span>Ciclos de vida</span>
						</button>
					)}
					{canShowSection("errores") && (
						<button
							type="button"
							className={activeSection === "errores" ? "is-active" : ""}
							onClick={() => setActiveSection("errores")}
						>
							<i className="ri-tools-fill" aria-hidden />
							<span>Errores comunes</span>
						</button>
					)}
					{canShowSection("columnas") && (
						<button
							type="button"
							className={activeSection === "columnas" ? "is-active" : ""}
							onClick={() => setActiveSection("columnas")}
						>
							<i className="ri-table-fill" aria-hidden />
							<span>Diccionario columnas</span>
						</button>
					)}
					{canShowSection("evitar") && (
						<button
							type="button"
							className={activeSection === "evitar" ? "is-active" : ""}
							onClick={() => setActiveSection("evitar")}
						>
							<i className="ri-forbid-2-fill" aria-hidden />
							<span>Qué no hacer</span>
						</button>
					)}
					{canShowSection("flujos") && (
						<button
							type="button"
							className={activeSection === "flujos" ? "is-active" : ""}
							onClick={() => setActiveSection("flujos")}
						>
							<i className="ri-route-fill" aria-hidden />
							<span>Flujos frecuentes</span>
						</button>
					)}
				</aside>

				<main className="guide20-content">
					{searchTerm && (
						<section className="guide20-panel">
							<h2>Resultados de búsqueda</h2>
							<p>
								Resultados para <strong>{searchTerm}</strong>. Selecciona un resultado para abrir
								la sección relacionada.
							</p>
							{searchResults.length > 0 ? (
								<div className="guide20-search-results">
									{searchResults.map((result) => (
										<button
											type="button"
											key={`${result.type}-${result.title}`}
											onClick={() => goToGuideSection(result.section)}
										>
											<span>{result.type}</span>
											<strong>{result.title}</strong>
											<small>{result.description}</small>
										</button>
									))}
								</div>
							) : (
								<div className="guide20-empty-result">
									<i className="ri-search-eye-line" aria-hidden />
									<p>No se encontraron coincidencias. Prueba con otro término del sistema.</p>
								</div>
							)}
						</section>
					)}

					{activeSection === "resumen" && (
						<>
							<section className="guide20-panel">
								<h2>Resumen ejecutivo</h2>
								<p>
									{roleConfig.overview}
								</p>
								<div className="guide20-rule-grid">
									{quickRules.map((rule) => (
										<article className="guide20-rule-card" key={rule.title}>
											<div className="guide20-rule-title">
												<i className={rule.icon} aria-hidden />
												<h3>{rule.title}</h3>
											</div>
											<ul>
												<span className="fs-6">{rule.items.map((item) => (
													<li key={item}>{item}</li>
												))}</span>
											</ul>
										</article>
									))}
								</div>
							</section>

							<section className="guide20-panel">
								<h2>Matriz de estados del curso</h2>
								<div className="guide20-table-wrap">
									<table className="guide20-table">
										<thead>
											<tr>
												<th><span>Estado</span></th>
												<th><span>Admin</span></th>
												<th><span>Docente</span></th>
												<th><span>Estudiante</span></th>
												<th><span>Representante</span></th>
											</tr>
										</thead>
										<tbody>
											{courseStateMatrix.map((row) => (
												<tr key={row.state}>
													<td>
														<span className="status status-open" >{row.state}</span>
													</td>
													<td><span className="fs-6">{row.admin}</span></td>
													<td><span className="fs-6">{row.teacher}</span></td>
													<td><span className="fs-6">{row.student}</span></td>
													<td><span className="fs-6">{row.company}</span></td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</section>
						</>
					)}

					{activeModule && (
						<section className="guide20-panel">
							<div className="guide20-module-header">
								<div>
									<p className="guide20-eyebrow">Modulo del sistema</p>
									<h2>
										<i className={activeModule.icon} aria-hidden /> {activeModule.title}
									</h2>
									<p>{activeModule.purpose}</p>
								</div>
								{roleConfig.canOpenModules && (
									<button
										type="button"
										className="admin-btn"
										onClick={() => goToModule(activeModule.path)}
									>
										<i className="ri-arrow-right-up-fill me-2" style={{ color: "var(--font)" }} aria-hidden />
										<span>Abrir módulo</span>
									</button>
								)}
							</div>

							<div className="guide20-detail-grid">
								<span style={{ display: 'contents' }}>
									<SectionList title="Acciones principales" items={activeModule.actions} />
									<SectionList title="Reglas de negocio" items={activeModule.rules} />
									<SectionList title="Preguntas de soporte" items={activeModule.support} />
								</span>
							</div>

							<span><ProcedureExplorer key={activeModule.id} module={activeModule} /></span>
						</section>
					)}

					{canShowSection("glosario") && activeSection === "glosario" && (
						<section className="guide20-panel">
							<h2>Glosario de estados y conceptos</h2>
							<p>
								Usa está sección para distinguir términos que se parecen, pero que controlan
								reglas distintas dentro del sistema.
							</p>
							<div className="guide20-glossary-grid">
								{glossaryTerms.map((item) => (
									<article className="guide20-info-card" key={item.term}>
										<h3>{item.term}</h3>
										<p>{item.meaning}</p>
										<small>{item.example}</small>
									</article>
								))}
							</div>
						</section>
					)}

					{canShowSection("permisos") && activeSection === "permisos" && (
						<section className="guide20-panel">
							<h2>Matriz de permisos por rol</h2>
							<p>
								Esta tabla resume que puede hacer cada tipo de usuario y donde deben aplicarse
								los bloqueos de negocio.
							</p>
							<div className="guide20-table-wrap">
								<table className="guide20-table">
									<thead>
										<tr>
											<th><span>Rol</span></th>
											<th><span>Puede ver</span></th>
											<th><span>Puede crear</span></th>
											<th><span>Puede editar</span></th>
											<th><span>Restricciones</span></th>
										</tr>
									</thead>
									<tbody>
										{permissionMatrix
											.filter((row) => guideRole === "admin" || normalizeGuideRole(row.role) === guideRole)
											.map((row) => (
												<tr key={row.role}>
													<td>
														<span className="status status-open">{row.role}</span>
													</td>
													<td><span>{row.view}</span></td>
													<td><span>{row.create}</span></td>
													<td><span>{row.edit}</span></td>
													<td><span>{row.restrictions}</span></td>
												</tr>
											))}
									</tbody>
								</table>
							</div>
						</section>
					)}

					{canShowSection("casos") && activeSection === "casos" && (
						<section className="guide20-panel">
							<h2>Casos frecuentes</h2>
							<p>
								Consultas comunes para responder rapido sin revisar código ni base de datos en
								primera instancia.
							</p>
							<div className="guide20-faq-list">
								{supportCases.map((item) => (
									<article className="guide20-faq-item" key={item.question}>
										<h5>{item.question}</h5>
										<p>{item.answer}</p>
									</article>
								))}
							</div>
						</section>
					)}

					{canShowSection("reglas") && activeSection === "reglas" && (
						<section className="guide20-panel">
							<h2>Reglas críticas del negocio</h2>
							<p>
								Estas reglas deben mantenerse coherentes en backend, frontend y datos de prueba.
							</p>
							<div className="guide20-critical-list">
								{criticalBusinessRules.map((rule, index) => (
									<div className="guide20-critical-item" key={rule}>
										<span className="btn-admin">{index + 1}</span>
										<p>{rule}</p>
									</div>
								))}
							</div>
						</section>
					)}

					{canShowSection("decisiones") && activeSection === "decisiones" && (
						<section className="guide20-panel">
							<h2>Indice de decisiones</h2>
							<p>
								Guia rápida para decidir que revisar cuando una acción no está disponible o un
								usuario reporta un bloqueo.
							</p>
							<div className="guide20-decisión-grid">
								{decisionIndex.map((item) => (
									<article className="guide20-decisión-card" key={item.situation}>
										<div className="guide20-rule-title">
											<i className="ri-checkbox-blank-circle-fill" aria-hidden />
											<h5>{item.situation}</h5>
										</div>
										<dl>
											<div>
												<dt><span className="fs-6">Revisa</span>r</dt>
												<dd><span className="fs-6">{item.check}</span></dd>
											</div>
											<div>
												<dt><span className="fs-6">Ir a</span></dt>
												<dd><span className="fs-6">{item.goTo}</span></dd>
											</div>
										</dl>
									</article>
								))}
							</div>
						</section>
					)}

					{canShowSection("ciclos") && activeSection === "ciclos" && (
						<section className="guide20-panel">
							<h2>Mapas de ciclo de vida</h2>
							<p>
								Estos mapas explican como avanza un curso y como cambia la matrícula de un
								estudiante durante la operación academica.
							</p>
							<div className="guide20-lifecycle-grid">
								<article className="guide20-lifecycle">
									<h3>Ciclo de vida del curso</h3>
									<div className="guide20-lifecycle-steps">
										{courseLifecycle.map((item) => (
											<div className="guide20-lifecycle-step" key={item.state}>
												<span className="btn-admin">{item.state}</span>
												<p className="mt-2">
													<strong>Permite:</strong> {item.allowed}
												</p>
												<p>
													<strong>Bloquea:</strong> {item.blocked}
												</p>
												<span><small>Siguiente: {item.next}</small></span>
											</div>
										))}
									</div>
								</article>
								<article className="guide20-lifecycle">
									<h3>Ciclo de vida del estudiante</h3>
									<div className="guide20-lifecycle-steps">
										{studentLifecycle.map((item) => (
											<div className="guide20-lifecycle-step" key={item.state}>
												<span className="btn-admin">{item.state}</span>
												<p className="mt-2">{item.meaning}</p>
												<span><small>Siguiente: {item.next}</small></span>
											</div>
										))}
									</div>
								</article>
							</div>
						</section>
					)}

					{canShowSection("errores") && activeSection === "errores" && (
						<section className="guide20-panel">
							<h2>Errores comunes y solución</h2>
							<p>
								Tabla de apoyo para diagnosticar bloqueos frecuentes y decidir donde revisar.
							</p>
							<div className="guide20-table-wrap">
								<table className="guide20-table">
									<thead>
										<tr>
											<th><span>Problema</span></th>
											<th><span>Causa probable</span></th>
											<th><span>Dónde revisar</span></th>
											<th><span>Accion recomendada</span></th>
										</tr>
									</thead>
									<tbody>
										{commonIssues.map((issue) => (
											<tr key={issue.problem}>
												<td><span>{issue.problem}</span></td>
												<td><span>{issue.cause}</span></td>
												<td><span>{issue.where}</span></td>
												<td><span>{issue.action}</span></td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</section>
					)}

					{canShowSection("evitar") && activeSection === "evitar" && (
						<section className="guide20-panel">
							<h2>Qué no hacer</h2>
							<p>
								Practicas que pueden generar inconsistencias, bloqueos sin sentido o pérdida de
								trazabilidad operativa.
							</p>
							<div className="guide20-critical-list">
								{doNotRules.map((rule, index) => (
									<div className="guide20-critical-item guide20-danger-item" key={rule}>
										<span className="btn-admin">{index + 1}</span>
										<p>{rule}</p>
									</div>
								))}
							</div>
						</section>
					)}

					{canShowSection("columnas") && activeSection === "columnas" && (
						<section className="guide20-panel">
							<h2>Diccionario de columnas</h2>
							<p>
								Referencia rápida para explicar que significa cada columna recurrente del sistema.
							</p>
							<div className="guide20-table-wrap">
								<table className="guide20-table">
									<thead>
										<tr>
											<th><span>Columna</span></th>
											<th><span>Significado</span></th>
											<th><span>Modulos donde aparece</span></th>
										</tr>
									</thead>
									<tbody>
										{columnDictionary.map((item) => (
											<tr key={item.column}>
												<td>
													<span className="status status-open">{item.column}</span>
												</td>
												<td><span>{item.meaning}</span></td>
												<td><span>{item.modules}</span></td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</section>
					)}

					{canShowSection("flujos") && activeSection === "flujos" && (
						<section className="guide20-panel">
							<h2>Flujos frecuentes y checklists</h2>
							<p>
								Estos flujos sirven como checklist para responder dudas operativas sin revisar
								código ni base de datos.
							</p>
							<div className="guide20-workflow-grid">
								{workflows.map((workflow) => (
									<article className="guide20-workflow" key={workflow.title}>
										<h3>{workflow.title}</h3>
										<ol>
											<span>{workflow.steps.map((step) => (
												<li key={step}>{step}</li>
											))}</span>
										</ol>
									</article>
								))}
							</div>
						</section>
					)}
				</main>
			</div>
		</div>
	);
}

